import { MediaList } from "./components/MediaList";
import { Nav, regexIsNodeContentScene } from "./components/Nav";
import { MediaDetailContainer } from "./components/MediaDetail";
import { Actions } from "./components/Actions";
import { createContext, useContext, useEffect, useRef, useState, useMemo } from "react";
import { shallowEqual } from "react-redux";
import type { AppDispatch, RootState } from "../../common/state/store";
import type { IMedia } from "../../model/unityObject";
import {
  setCurrentMediaSidebar,
  getCurrentMediaGalleryId,
  getOriginalMediaGalleryId,
  setCurrentMediaGallery,
  getUploadedFile,
  setUploadedFile,
  setUploadedOldFile,
  getCurrentModeListGallery,
  fetchMediaSearchIndex,
  fetchMediaTags,
  getFilteredMedias,
  fetchFilteredMedias,
  getMediaSearch,
  getMediaSearchIndex,
} from "./state/gallerySlice";
import { currentOrgSelector } from "../../common/state/selectors/authSelector";
import {
  closeConfirmationModal,
  closeSlider,
  openConfirmationModal,
  openSlider,
} from "../../common/state/slice/modal/modalSlice";
import ModalConfirmation from "../../common/components/Modal/ModalConfirmation";
import { useTranslation } from "react-i18next";
import { GenerateMediaPanel } from "./components/GenerateMediaPanel";
import { UploadPanel, UPLOAD_SLIDEOVER_COMPONENT_ID } from "./components/UploadPanel";
import { getNodeById } from "../graph/state/graphLegacy";
import { useLocation } from "react-router-dom";
import { isStringNullOrEmpty } from "src/common/util/isStringNullOrEmpty";
import {
  computeUploadPanelParams,
  deduceMediaType,
  fromMediaTypeInferInputAcceptParam,
} from "./state/uploadUtil";
import { setAlert } from "../alert/state/alertsSlice";
import { FolderDownloadIcon } from "@heroicons/react/outline";
import { PaginationControls } from "src/common/components/PaginationControls/PaginationControls";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import { LoadingState } from "src/common/state/slice/appStateSlice";
import { IsLoading, useLoading } from "src/common/components/AppState/IsLoading";
import { useGalleryPagination } from "./useGalleryPagination";
import { isGifMedia } from "./util/mediaTypeUtil";

export const MEDIA_LIST_COMPONENT_ID = "mediaListComponent";

const NestedGalleryContext = createContext(false);

export const Gallery = ({
  isMediaPickerMode = false,
  onConfirmSelectMedia,
  onCancel,
  nodeId,
  isModalEdit,
  contentType,
  title = "",
  allowedMediaTypeOverride,
  allowedMedia360TagOverride,
  isModalGallery = false,
  uploadAcceptOverride,
  onUploadedFileSelect,
}: {
  isModalEdit?: boolean;
  projectId?: string;
  // We can use the gallery to select media in graph explorer. Set this to true to do it
  isMediaPickerMode?: boolean;
  onConfirmSelectMedia?: (media: IMedia) => void;
  onCancel?: () => void;
  nodeId?: string;
  contentType?: string;
  title?: string;
  allowedMediaTypeOverride?: string;
  allowedMedia360TagOverride?: string;
  isModalGallery?: boolean;
  uploadAcceptOverride?: string;
  onUploadedFileSelect?: (file: File) => void;
}) => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useAppDispatch();
  const handlePageChange = useGalleryPagination();
  const { pathname } = useLocation();

  const wrapperRef = useRef<HTMLDivElement>(null);
  const navbarRef = useRef<HTMLDivElement>(null);

  const nodeTypeScene = useAppSelector((state: RootState) =>
    getNodeById(state, String(nodeId), contentType),
  );

  const sourceMediasIndex = useAppSelector(getMediaSearchIndex);
  const { page, limit } = useAppSelector(getMediaSearch);

  const currentNode = useAppSelector((state: RootState) =>
    Number(nodeId) !== 0 ? getNodeById(state, nodeId ? nodeId : "-1") : null,
  );
  const isNodeContentScene = currentNode?.modelName === "content-scene";
  const isContentSceneContext = regexIsNodeContentScene(pathname) || isNodeContentScene;

  const filteredSourceMediasIndex = useMemo(
    () =>
      isContentSceneContext
        ? sourceMediasIndex.filter((media) => !isGifMedia(media))
        : sourceMediasIndex,
    [sourceMediasIndex, isContentSceneContext],
  );

  const totalSourceMedias = filteredSourceMediasIndex.length;

  const totalPages = Math.max(1, Math.ceil(totalSourceMedias / (limit ?? 20)));

  const Modal_ID = "media-detail-slideover";

  const [isDragging, setIsDragging] = useState(false);
  const [opacity, setOpacity] = useState("opacity-0");

  const { filteredMedias, originalMediaGalleryId, currentMediaGalleryId, org } = useAppSelector(
    (state: RootState) => {
      return {
        filteredMedias: getFilteredMedias(state),
        originalMediaGalleryId: getOriginalMediaGalleryId(state),
        currentMediaGalleryId: getCurrentMediaGalleryId(state),
        org: currentOrgSelector(state),
      };
    },
    shallowEqual,
  );

  useEffect(() => {
    if (org?.id === undefined) return;
    void dispatch(fetchFilteredMedias({ limit: 20, componentId: MEDIA_LIST_COMPONENT_ID }));
    void dispatch(fetchMediaTags({ orgId: Number(org.id) }));
  }, [org?.id]);

  const currentModeListGallery = useAppSelector(getCurrentModeListGallery);
  const uploading = useAppSelector(getUploadedFile);
  const isNestedInGallery = useContext(NestedGalleryContext);
  const currentMediaSearch = useAppSelector(getMediaSearch, shallowEqual);
  const galleryLoadingState = useLoading(MEDIA_LIST_COMPONENT_ID);
  const isGalleryLoading = galleryLoadingState !== LoadingState.Success;

  const openMediaDetail = (mediaId: number) => {
    dispatch(openSlider(Modal_ID));
    dispatch(setCurrentMediaSidebar(mediaId));
  };

  const ownsGlobalSlider = !(isModalGallery && isNestedInGallery);

  useEffect(() => {
    dispatch(setUploadedFile(undefined));
    dispatch(setUploadedOldFile(undefined));
    if (ownsGlobalSlider) {
      dispatch(closeSlider(""));
    }
    if (!isModalGallery) {
      void dispatch(fetchMediaSearchIndex({}));
    }
  }, [isModalGallery, ownsGlobalSlider]);

  useEffect(() => {
    if (wrapperRef && wrapperRef.current) {
      wrapperRef.current.scrollIntoView();
    }
  }, [currentMediaSearch]);

  useEffect(() => {
    dispatch(setCurrentMediaGallery(originalMediaGalleryId));

    const sc = () => {
      const el = document?.getElementById("gallery-selected-media-target");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
      } else {
        setTimeout(sc, 100);
      }
    };

    if (nodeId) {
      sc();
    }
  }, [originalMediaGalleryId]);

  const onCancelWrapper = () => {
    if (Number(originalMediaGalleryId) !== Number(currentMediaGalleryId)) {
      dispatch(openConfirmationModal("confirmCloseGallery"));
    } else {
      if (onCancel) {
        dispatch(closeConfirmationModal("confirmCloseGallery"));
        onCancel();
      }
    }
  };

  useEffect(() => {
    if (!ownsGlobalSlider) return;
    if (uploading) {
      dispatch(openSlider(UPLOAD_SLIDEOVER_COMPONENT_ID));
    } else {
      dispatch(closeSlider(UPLOAD_SLIDEOVER_COMPONENT_ID));
    }
  }, [uploading, ownsGlobalSlider]);

  /**
   * Filters media based on current context and upload status
   * For content-scene contexts: Excludes GIFs and transcoding media
   * For other contexts: Excludes only transcoding media
   * @returns Filtered array of displayable media
   */
  const getFilteredMediaForContext = () => {
    if (isContentSceneContext) {
      return filteredMedias.filter(
        (media: IMedia) => media.upload_status !== "transcoding" && !isGifMedia(media),
      );
    } else {
      return filteredMedias.filter((media: IMedia) => media.upload_status !== "transcoding");
    }
  };

  const dynamicWarningDragAndDrop = () => {
    if (!nodeId) {
      return t("general.extensionNotAccepted");
    }

    switch (nodeTypeScene?.modelName) {
      case "content-video-2-d":
        return t("general.extensionNotAcceptedVideo2d");
      case "content-audio":
        return t("general.extensionNotAcceptedAudio");
      case "content-scene":
        return currentMediaSearch?.mediaType === "model3d"
          ? t("general.extensionNotAccepted3d")
          : t("general.extensionNotAcceptedImageAndVideo");
      case "content-thumbnail":
      case "content-onboarding":
        return t("general.extensionNotAcceptedImage");
      case "content-3d-viewer":
      case "content-3-d-viewer":
        return t("general.extensionNotAccepted3d");
      default:
        return t("general.extensionNotAccepted");
    }
  };

  const alertExtension = () => {
    dispatch(
      setAlert({
        msg: dynamicWarningDragAndDrop(),
        type: "warning",
        isOpen: true,
        customTimeoutInMs: 20000,
      }),
    );
  };

  let selectedFileType = "all";
  if (currentNode) {
    if (currentNode.modelName === "content-scene" && currentMediaSearch?.mediaType !== "model3d") {
      selectedFileType = "content-scene";
    } else {
      selectedFileType = currentMediaSearch?.mediaType;
    }
  }
  if (regexIsNodeContentScene(pathname)) {
    selectedFileType = "content-scene";
  }

  const isNodeContentSceneOrRegex = isNodeContentScene || regexIsNodeContentScene(pathname);

  const isExternalFileDrag = (event: React.DragEvent) => {
    if (!event.dataTransfer) return false;
    return Array.prototype.some.call(
      event.dataTransfer.types,
      (type: string) => type === "Files" || type === "application/x-moz-file",
    );
  };

  const handleDragEnter = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isExternalFileDrag(event)) return;
    setTimeout(() => {
      setOpacity("opacity-1");
    }, 300);
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setOpacity("opacity-0");
    setIsDragging(false);
  };

  const handleDragOver = (event: React.DragEvent) => {
    if (!isExternalFileDrag(event)) return;
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);
    setOpacity("opacity-0");

    if (!event.dataTransfer?.files?.length) return;

    const file = event.dataTransfer.files[0];
    const extension = file?.name.slice(file?.name.lastIndexOf("."));

    const testIfExtensionIsGif = () => {
      if (isNodeContentSceneOrRegex) {
        return isGifMedia({ type: file?.type, filename: file?.name });
      }
      return false;
    };

    let currentFileType = event.dataTransfer.files[0].type;
    if (isStringNullOrEmpty(currentFileType)) {
      const splitFileName = event.dataTransfer.files[0].name.split(".");
      if (splitFileName.length > 0) {
        try {
          const type = deduceMediaType(splitFileName[splitFileName.length - 1]);
          currentFileType = type + "/" + splitFileName[splitFileName.length - 1];
        } catch {
          currentFileType = "";
        }
      }
    }

    const splitInArray = (
      uploadAcceptOverride ?? fromMediaTypeInferInputAcceptParam(selectedFileType)
    ).split(",");
    const testExtensions = !splitInArray.includes(currentFileType);

    if (testExtensions || extension === ".m4v" || testIfExtensionIsGif()) {
      alertExtension();
    } else if (onUploadedFileSelect) {
      onUploadedFileSelect(event.dataTransfer.files[0]);
    } else {
      const uploadedFile = event.dataTransfer.files[0];
      const cupp = computeUploadPanelParams({ file: uploadedFile, from: "graph" });
      dispatch(setUploadedFile({ file: uploadedFile, metadata: cupp }));
    }
  };

  return (
    <div>
      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={`absolute block flex w-full h-full ${
          isDragging
            ? `dragging bg-white ${opacity} ease-in-out duration-[900ms] border-2 border-green-500`
            : ""
        }`}
      >
        {/* hierarchy problem with divs and the draging is disabled when colliding with other divs */}
        {isDragging ? (
          <div className="pointer-events-none w-ful h-full mx-auto">
            <div className="pointer-events-none italic text-5xl text-white flex flex-col items-center mt-96 font-bold z-[999] text-gray-500 ">
              {t("general.dragFilesHere")}
              <div className="text-xl mt-2 text-gray-500">{t("general.dragAndDropText")}</div>

              <FolderDownloadIcon className="w-20 h-20 stroke-green-500 mt-6" />
            </div>
          </div>
        ) : (
          <div ref={wrapperRef} className="flex w-full h-full">
            <div className="w-full gallery h-full flex flex-col">
              <div ref={navbarRef}>
                <Nav
                  title={title}
                  nodeId={nodeId ?? "0"}
                  allowedMediaTypeOverride={allowedMediaTypeOverride}
                  allowedMedia360TagOverride={allowedMedia360TagOverride}
                  hideGenerateButton={!ownsGlobalSlider}
                  uploadAcceptOverride={uploadAcceptOverride}
                  onUploadedFileSelect={onUploadedFileSelect}
                  isModalGallery={isModalGallery}
                />
              </div>

              <div
                className={`flex flex-col flex-grow overflow-y-auto h-full  mediaListWrapper ${
                  currentModeListGallery && !isModalGallery ? "p-2" : ""
                }`}
              >
                <IsLoading componentId={MEDIA_LIST_COMPONENT_ID} showSuccess={false}>
                  <MediaList
                    medias={getFilteredMediaForContext()}
                    isModalEdit={isModalEdit}
                    listMode={isModalGallery ? false : currentModeListGallery}
                    openMediaDetail={openMediaDetail}
                    currentMedia={currentMediaGalleryId}
                    isMediaPickerMode={isMediaPickerMode}
                  />
                </IsLoading>
              </div>

              <ModalConfirmation
                title={t("pages.gallery.cancelConfirmationModal.title")}
                text={t("pages.gallery.cancelConfirmationModal.body")}
                onClickSubmit={() => {
                  dispatch(closeConfirmationModal("confirmCloseGallery"));
                  if (onCancel) {
                    onCancel();
                  }
                }}
                id={"confirmCloseGallery"}
              />

              <IsLoading componentId={MEDIA_LIST_COMPONENT_ID} showSuccess={false}>
                <div className="h-[4rem] w-full border-t ">
                  <PaginationControls
                    currentPage={page}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    isLoading={isGalleryLoading}
                    className="w-[97%] self-center"
                    galleryMode
                  />
                </div>
              </IsLoading>

              {onConfirmSelectMedia && (
                <Actions onConfirmSelectMedia={onConfirmSelectMedia} onCancel={onCancelWrapper} />
              )}
            </div>

            <MediaDetailContainer id={Modal_ID} large={false} />
            <UploadPanel uploadFromSelectionCb={onConfirmSelectMedia} />
            {!(isModalGallery && isNestedInGallery) && (
              <NestedGalleryContext.Provider value={true}>
                <GenerateMediaPanel />
              </NestedGalleryContext.Provider>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
